import { useState } from 'react';

import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  CopyButton,
  Group,
  rem,
  Text,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconCircle,
  IconCopy,
  IconX,
} from '@tabler/icons-react';

import { Order, PaymentType } from '@interfaces/orders.interface';

import {
  useConfirmOrderBankTransferMutation,
  useCreateMolliePaymentMutation,
} from '@api/orders.api';

import classes from './OrderPaymentSection.module.css';

export interface OrderPaymentSectionProps {
  order: Order;
}

export default function OrderPaymentSection({
  order,
}: OrderPaymentSectionProps) {
  // ==========================================================================
  // General
  // ==========================================================================

  // ==========================================================================
  // State
  // ==========================================================================
  const [paymentType, setPaymentType] = useState<PaymentType>();

  const [isLoading, setIsLoading] = useState(false);

  const [confirmOrderBankTransfer] = useConfirmOrderBankTransferMutation();

  const [createMolliePayment] = useCreateMolliePaymentMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleSubmit = async () => {
    setIsLoading(true);

    if (paymentType === 'bankTransferOnConfirm') {
      try {
        await confirmOrderBankTransfer({ id: order.id }).unwrap();
        showNotification({
          title: 'Ordine confermato',
          message: 'Ordine confermato con successo',
        });
      } catch (e) {
        console.error(e);
        showNotification({
          title: 'Errore',
          message: "Errore nella conferma dell'ordine. Riprova",
          color: 'red',
          icon: <IconX />,
        });
      }
      setIsLoading(false);
    } else if (paymentType === 'onlineMollie') {
      try {
        const { checkoutUrl } = await createMolliePayment({
          orderId: order.id,
        }).unwrap();
        window.location.href = checkoutUrl;
      } catch (e) {
        console.error(e);
        showNotification({
          title: 'Errore',
          message: 'Errore nella creazione del pagamento. Riprova',
          color: 'red',
          icon: <IconX />,
        });
        setIsLoading(false);
      }
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Alert title="Ordine in attesa di pagamento" icon={<IconAlertCircle />}>
      <Text>
        Il tuo ordine è in attesa di pagamento. Scegli un metodo di pagamento e
        prosegui per confermare l'ordine.
      </Text>

      <Box mt="lg" bg="#fff">
        <Accordion
          variant="contained"
          chevronPosition="left"
          disableChevronRotation
          chevron={<IconCircle />}
          classNames={{ item: classes.accordionItem }}
          onChange={(value) => {
            if (value) {
              setPaymentType(value as PaymentType);
            }
          }}
        >
          <Accordion.Item value="onlineMollie">
            <Accordion.Control
              disabled={paymentType === 'onlineMollie'}
              className={classes.accordionControl}
            >
              Pagamento online
              {(order.paymentType === 'bankTransferOnConfirm' ||
                order.paymentType === 'other') && (
                <Text display="inline" fw="600">
                  (sconto del 3%)
                </Text>
              )}
            </Accordion.Control>
            <Accordion.Panel>
              Pagamenti supportati: carta di credito, Paypal
            </Accordion.Panel>
          </Accordion.Item>
          {(order.paymentType === 'bankTransferOnConfirm' ||
            order.paymentType === 'other') && (
            <Accordion.Item value="bankTransferOnConfirm">
              <Accordion.Control
                disabled={paymentType === 'bankTransferOnConfirm'}
                className={classes.accordionControl}
              >
                Bonifico bancario
              </Accordion.Control>
              <Accordion.Panel>
                IBAN:{' '}
                <Group gap="xs">
                  <strong>IT28S0880760790000000033111</strong>
                  <CopyButton
                    value="IT28S0880760790000000033111"
                    timeout={2000}
                  >
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? 'Copiato' : 'Copia'}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={copied ? 'teal' : 'gray'}
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck style={{ width: rem(16) }} />
                          ) : (
                            <IconCopy style={{ width: rem(16) }} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
                Per maggiori informazioni su come effettuare i bonifici consulta
                i{' '}
                <Anchor
                  size="sm"
                  href="https://www.tuoteam.com/termini"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Termini e condizioni
                </Anchor>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>
      </Box>

      {!paymentType && (
        <Alert mt="md" mb="lg" variant="filled" color="yellow">
          Seleziona un metodo di pagamento per proseguire
        </Alert>
      )}

      <>
        <Button
          size="md"
          mt="lg"
          disabled={!paymentType}
          loading={isLoading}
          onClick={handleSubmit}
          className={classes.nextStepButton}
        >
          Conferma ordine
          {paymentType === 'onlineMollie' && ' e paga'}
        </Button>
      </>
    </Alert>
  );
}
